type NavigationEntries = Record<string, string>;

type NavigationBack = {
  url: string;
  delta: number;
};

const storageKey = "axl.school.navigation-history";
const maxEntries = 50;

const contentRoutes = [
  "/learn",
  "/library",
  "/course-preview",
  "/student-course-preview",
];
const untrackedRoutes = [
  "/login",
  "/login-token",
  "/login-qr",
  "/login-as",
  "/register",
  "/c",
  "/forgot-password",
  "/reset-password",
  "/confirm-email",
  "/confirm-invite",
  "/email",
  "/email-notification",
  "/locked",
];

const matchRoute = (url: string, route: string) =>
  url === route || url.startsWith(`${route}/`) || url.startsWith(`${route}?`);

const isContentUrl = (url: string) =>
  contentRoutes.some((x) => matchRoute(url, x));

const isTrackedUrl = (url: string) =>
  url.startsWith("/") &&
  !url.startsWith("//") &&
  !url.startsWith("/\\") &&
  !untrackedRoutes.some((x) => matchRoute(url, x));

const tryReadEntries = (): NavigationEntries => {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    const entries = raw ? JSON.parse(raw) : null;

    return entries && typeof entries === "object" ? entries : {};
  } catch {
    return {};
  }
};

const trySaveEntries = (entries: NavigationEntries) => {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(entries));

    return true;
  } catch {
    return false;
  }
};

export const getHistoryIndex = () => {
  const index = (window.history.state as { idx?: number } | null)?.idx;

  return typeof index === "number" && index > 0 ? index : 0;
};

export const pushNavigationUrl = (url: string, index: number) => {
  const entries = tryReadEntries();
  if (isTrackedUrl(url)) entries[index] = url;
  else delete entries[index];

  for (const key of Object.keys(entries)) {
    const entryIndex = Number(key);
    if (!(entryIndex <= index && entryIndex >= index - maxEntries))
      delete entries[key];
  }

  trySaveEntries(entries);
};

export const getNavigationBack = (
  currentUrl: string,
  currentIndex: number,
  fallback: string,
): NavigationBack => {
  const entries = tryReadEntries();
  for (let i = currentIndex - 1; i >= 0; i -= 1) {
    const url = entries[i];
    if (!url || url === currentUrl || !isTrackedUrl(url) || isContentUrl(url))
      continue;

    return { url, delta: i - currentIndex };
  }

  return { url: fallback, delta: 0 };
};
